<template>
  <div class="rise-in space-y-6">
    <!-- Header card -->
    <div class="card card-pad">
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between gap-5"
      >
        <div>
          <div class="flex items-center gap-3 flex-wrap">
            <h1 class="page-title">{{ projectName }}</h1>
            <span class="pill pill-brand">
              <font-awesome-icon icon="user" />
              {{ patientId }}
            </span>
          </div>
          <button
            v-if="patient && !patient.study_completed"
            class="link-brand text-sm mt-2 bg-transparent border-0 p-0 cursor-pointer focus:outline-none"
            @click="addNewVisit"
          >
            <font-awesome-icon icon="plus-circle" /> Add follow-up visit
          </button>
        </div>

        <div class="flex flex-col items-start md:items-end gap-3">
          <button
            v-if="patient"
            class="btn btn-md"
            :class="patient.study_completed ? 'btn-primary' : 'btn-secondary'"
            @click="toggleStudyCompleted"
          >
            <font-awesome-icon
              :icon="patient.study_completed ? 'user-md' : 'user-slash'"
            />
            {{
              patient.study_completed ? 'Check in patient' : 'Check out patient'
            }}
          </button>

          <div class="flex items-center gap-2">
            <span class="text-sm text-ink-soft font-medium"
              >Study data entry completed</span
            >
            <button
              class="toggle-track rounded-full transition-colors duration-200 relative focus:outline-none p-0 border-0 flex items-center cursor-pointer"
              :class="{ 'track-active': patient && patient.study_completed }"
              @click="toggleStudyCompleted"
            >
              <div
                class="toggle-knob rounded-full bg-white shadow-sm"
                :class="{ 'knob-active': patient && patient.study_completed }"
              ></div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Visits -->
    <div v-for="visit in visits" :key="visit.number" class="card card-pad">
      <div class="flex items-center gap-3 mb-5">
        <span
          class="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center text-sm font-bold"
          >{{ visit.number }}</span
        >
        <h2 class="section-title">Visit {{ visit.number }}</h2>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <router-link
          v-for="f in visit.forms"
          :key="f.key"
          :to="formRoute(f)"
          class="form-card card card-hover p-5 flex flex-col justify-between min-h-[140px] group"
        >
          <h3
            class="form-card-title text-base font-bold text-ink leading-tight"
          >
            {{ f.title }}
          </h3>
          <div class="mt-4">
            <span class="pill" :class="statusPill(f.status)">
              <span class="status-dot" :class="statusDot(f.status)"></span>
              {{ statusLabel(f.status) }}
            </span>
          </div>
        </router-link>
      </div>
    </div>

    <div v-if="!orderedForms.length" class="card card-pad text-ink-muted">
      No forms yet for this patient.
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { actionTypes, getterTypes } from 'store/patient'

export default {
  name: 'PatientOverview',
  computed: {
    ...mapGetters({
      patient: getterTypes.getCurrentPatient,
      forms: getterTypes.forms,
    }),
    projectSlug() {
      return this.$route.params.projectSlug
    },
    patientId() {
      return this.$route.params.patientId
    },
    orderedForms() {
      return Object.values(this.forms).sort((a, b) =>
        a.key.localeCompare(b.key, undefined, { numeric: true })
      )
    },
    nextVisitNumber() {
      let maxVisit = 0
      Object.keys(this.forms).forEach(k => {
        const match = k.match(/visit(\d+)/)
        if (match) {
          const v = parseInt(match[1], 10)
          if (v > maxVisit) maxVisit = v
        }
      })
      return maxVisit + 1
    },
    projectName() {
      const slug = (this.projectSlug || '').toLowerCase()
      if (slug === 'bex') return 'BigExBrEss'
      if (slug === 'bro') return 'UszBro'
      if (slug === 'trx') return 'TripleEx'
      return this.projectSlug ? this.projectSlug.toUpperCase() : ''
    },
    visits() {
      const groups = {}
      this.orderedForms.forEach(f => {
        const visitNum = (f.key.match(/(\d+)/) || [])[1] || '1'
        if (!groups[visitNum]) {
          groups[visitNum] = []
        }
        groups[visitNum].push(f)
      })
      return Object.keys(groups)
        .sort((a, b) => Number(a) - Number(b))
        .map(num => ({
          number: num,
          forms: groups[num],
        }))
    },
  },
  created() {
    this.$store.dispatch(actionTypes.checkInPatient, this.patientId)
    this.$store.dispatch(actionTypes.subscribeForms, this.patientId)
    this.$store.dispatch(actionTypes.subscribePatient, this.patientId)
  },
  beforeDestroy() {
    this.$store.dispatch(actionTypes.unsubscribe)
  },
  methods: {
    statusPill(s) {
      if (s === 'finished') return 'pill-green'
      if (s === 'inProgress') return 'pill-amber'
      return 'pill-muted'
    },
    statusDot(s) {
      if (s === 'finished') return 'dot-green'
      if (s === 'inProgress') return 'dot-amber'
      return 'dot-gray'
    },
    statusLabel(s) {
      if (s === 'finished') return 'Completed'
      if (s === 'inProgress') return 'In progress'
      return 'Not started'
    },
    formRoute(f) {
      const params = {
        projectSlug: this.projectSlug,
        patientId: this.patientId,
      }
      const visitNumber = (f.key.match(/(\d+)/) || [])[1] || '1'
      if (f.key.startsWith('screening'))
        return { name: 'Screening', params: { ...params, visitNumber } }
      if (f.key.endsWith('-data'))
        return { name: 'Upload', params: { ...params, visitNumber } }
      return { name: 'Visit', params: { ...params, visitNumber } }
    },
    async addNewVisit() {
      try {
        await this.$store.dispatch(actionTypes.addVisit, {
          patientId: this.patientId,
          numberOfVisit: this.nextVisitNumber,
        })
      } catch (e) {
        alert('Failed to add visit: ' + e.message)
      }
    },
    async toggleStudyCompleted() {
      if (!this.patient) return
      try {
        await this.$store.dispatch(
          actionTypes.completeStudy,
          !this.patient.study_completed
        )
      } catch (e) {
        alert('Failed to update study completion: ' + e.message)
      }
    },
  },
}
</script>

<style scoped>
.form-card:hover .form-card-title {
  color: #831f82;
}
.dot-green {
  background-color: #16a34a;
}
.dot-amber {
  background-color: #f59e0b;
}
.dot-gray {
  background-color: #94a3b8;
}
.toggle-track {
  width: 42px;
  height: 22px;
  background-color: #cbd5e1;
}
.track-active {
  background-color: #831f82;
}
.toggle-knob {
  width: 18px;
  height: 18px;
  margin-left: 2px;
  transition: margin-left 0.2s ease-in-out;
}
.knob-active {
  margin-left: 22px;
}
</style>
